import React, { CSSProperties, PropsWithChildren, MouseEvent } from "react";
import cn from "classnames";

import styles from "./Draggable.module.scss";

interface State {
    readyToDrag: boolean;
    isDragging: boolean;
    originalX: number;
    originalY: number;
    translateX: number;
    translateY: number;

    lastTranslateX: number;
    lastTranslateY: number;
}

interface Props {
    onDragStart?: (props: { clientX: number; clientY: number }) => void;
    onDrag?: (props: { translateX: number; translateY: number; clientX: number; clientY: number }) => void;
    onDragEnd?: () => void;
    onlyX?: boolean;
    onlyY?: boolean;
    bounceBack?: boolean;
    minX?: number;
    maxX?: number;
    minY?: number;
    maxY?: number;
    dragDelayDist?: number; // distance mouse needs to move to start dragging action
}

class Draggable extends React.Component<Props & { innerRef?: React.Ref<HTMLDivElement> }, State> {
    state = {
        readyToDrag: false,
        isDragging: false,

        originalX: 0,
        originalY: 0,

        translateX: 0,
        translateY: 0,

        lastTranslateX: 0,
        lastTranslateY: 0
    };

    componentWillUnmount(): void {
        window.removeEventListener("mousemove", (this.handleMouseMove as unknown) as EventListener);
        window.removeEventListener("mouseup", this.handleMouseUp);
    }

    handleMouseDown = ({ clientX, clientY }: MouseEvent<HTMLDivElement>): void => {
        window.addEventListener("mousemove", (this.handleMouseMove as unknown) as EventListener);
        window.addEventListener("mouseup", this.handleMouseUp);

        this.setState({
            readyToDrag: true,
            originalX: clientX,
            originalY: clientY
        });
    };

    handleMouseMove = ({ clientX, clientY }: MouseEvent<HTMLDivElement>): void => {
        const { readyToDrag, isDragging, originalX, originalY } = this.state;
        const { onDrag, onlyX, onlyY, minX, maxX, minY, maxY, dragDelayDist } = this.props;

        if (!(readyToDrag || isDragging)) {
            return;
        }

        if (readyToDrag && dragDelayDist) {
            // delay start of dragging until some distance
            const distSq =
                (originalX - clientX) * (originalX - clientX) + (originalY - clientY) * (originalY - clientY);
            if (distSq < dragDelayDist) {
                return;
            }
        }

        if (readyToDrag) {
            if (this.props.onDragStart) {
                this.props.onDragStart({ clientX, clientY });
            }

            this.setState({
                isDragging: true,
                readyToDrag: false
            });
        }

        this.setState(
            (prevState) => {
                if (minX && clientX < minX) {
                    clientX = minX;
                }
                if (maxX && clientX > maxX) {
                    clientX = maxX;
                }
                if (minY && clientY < minY) {
                    clientY = minY;
                }
                if (maxY && clientY > maxY) {
                    clientY = maxY;
                }

                const translateX = clientX - prevState.originalX + prevState.lastTranslateX;
                const translateY = clientY - prevState.originalY + prevState.lastTranslateY;
                if (onlyX) return { translateX, translateY: prevState.translateY };
                if (onlyY) return { translateX: prevState.translateX, translateY };
                return { translateX, translateY };
            },
            () => {
                if (onDrag) {
                    onDrag({
                        translateX: this.state.translateX,
                        translateY: this.state.translateY,
                        clientX,
                        clientY
                    });
                }
            }
        );
    };

    handleMouseUp = (): void => {
        window.removeEventListener("mousemove", (this.handleMouseMove as unknown) as EventListener);
        window.removeEventListener("mouseup", this.handleMouseUp);

        if (this.props.bounceBack) {
            this.setState({ isDragging: false, translateX: 0, translateY: 0 }, () => {
                if (this.props.onDragEnd) {
                    this.props.onDragEnd();
                }
            });
        } else {
            this.setState(
                {
                    originalX: 0,
                    originalY: 0,
                    lastTranslateX: this.state.translateX,
                    lastTranslateY: this.state.translateY,

                    isDragging: false
                },
                () => {
                    if (this.props.onDragEnd) {
                        this.props.onDragEnd();
                    }
                }
            );
        }
    };

    render(): React.ReactElement {
        const { children, innerRef } = this.props;
        const { translateX, translateY, isDragging } = this.state;

        return (
            <div ref={innerRef} className={cn({ [styles.isDragging]: isDragging })}>
                <ContainerWithRef
                    onMouseDown={this.handleMouseDown}
                    x={translateX}
                    y={translateY}
                    isDragging={isDragging}
                >
                    {children}
                </ContainerWithRef>
            </div>
        );
    }
}

const DraggableWithRef = React.forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => (
    <Draggable innerRef={ref} {...props} />
));

interface ContainerProps {
    x: number;
    y: number;
    isDragging: boolean;
    onMouseDown: (ev: MouseEvent<HTMLDivElement>) => void;
}

const Container: React.ForwardRefRenderFunction<HTMLDivElement, PropsWithChildren<ContainerProps>> = function (
    { x, y, isDragging, onMouseDown, children },
    ref
) {
    const style: CSSProperties = {
        transform: `translate(${x}px, ${y}px) ${isDragging ? "rotate(7deg)" : ""}`
    };
    return (
        <div
            ref={ref}
            className={cn({ [styles.containerIsDragging]: isDragging })}
            style={style}
            onMouseDown={onMouseDown}
        >
            {children}
        </div>
    );
};

const ContainerWithRef = React.forwardRef<HTMLDivElement, PropsWithChildren<ContainerProps>>(Container);

export default DraggableWithRef;
